import { IAlertTypeItemData, IGroupInfo } from "@home-health-notify/domain";
import { REFERRAL_STATUS } from "@home-health-notify/models";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, ListItem, Typography } from "@mui/material";
import {
  ApiErrorHandler,
  formatChatMessageTime,
  OrganizationContext,
  useFetchChatInfo,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import React, { useContext, useEffect, useState } from "react";
import { RightDrawerContext } from "../../context/RightDrawerContext";
import { PatientHeaderDescription } from "../../message/messageHeader/patientGroupHeader/PatientHeaderDescription";
import { MessageTimeComponent } from "../../message/messageType/MessageContentStyle";
import ReferralRefusalModal from "./ReferralRefusalModal";

interface ReferralAlertItemProps {
  groupInfo: IGroupInfo;
  referralItem: IAlertTypeItemData;
}

const ReferralAlertItem = ({
  groupInfo,
  referralItem,
}: ReferralAlertItemProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [modalData, setModalData] = useState<IAlertTypeItemData>();
  const [reasonOfRefusal, setReasonOfRefusal] = useState<string>("");
  const handleModalOpen = (referralItem: IAlertTypeItemData) => {
    setModalData(referralItem);
    setModalOpen(true);
  };
  const handleModalClose = () => {
    setModalOpen(false);
  };
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onAcceptReferral = async (referralItem: IAlertTypeItemData) => {
    if (isSubmitting || !referralItem.referralId) {
      return;
    }
    setIsSubmitting(true);
    try {
      await selectedOrgUser?.alert.handleReferralAlerts({
        referralId: referralItem.referralId,
        status: REFERRAL_STATUS.ACCEPTED,
        alertId: referralItem.alertId,
      });
      enqueueSnackbar(tNs("referral.acceptSnackbarMessage"));
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRefuseReferral = async (
    referralItem: IAlertTypeItemData,
    reason: string,
  ) => {
    if (isSubmitting || !referralItem.referralId) {
      return;
    }
    setIsSubmitting(true);
    try {
      await selectedOrgUser?.alert.handleReferralAlerts({
        alertId: referralItem.alertId,
        reason: reason,
        referralId: referralItem.referralId,
        status: REFERRAL_STATUS.DECLINED,
      });
      setModalOpen(false);
      enqueueSnackbar(tNs("referral.refuseSnackbarMessage"));
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!modalOpen) {
      setReasonOfRefusal("");
    }
  }, [modalOpen]);
  const { openChatInfoDrawer } = useContext(RightDrawerContext);
  const { setChatInfoContext } = useFetchChatInfo();

  return (
    <ListItem
      alignItems="flex-start"
      sx={{
        flexDirection: "column",
        cursor: "pointer",
      }}
      onClick={async () => {
        openChatInfoDrawer(0);
        // Setting chat info context for the referral group
        await setChatInfoContext(groupInfo.id, groupInfo.groupType);
      }}
      data-testid="pending-referral-list-item"
    >
      <PatientHeaderDescription
        patientStatus={groupInfo.patientStatus}
        meta={groupInfo.meta}
        servicesToPatient={groupInfo.servicesToPatient}
        name={groupInfo.name}
      />
      {referralItem.referralMessage &&
      referralItem.referralMessage.length > 0 ? (
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column"
            }}>
            <Typography
              variant="subtitle1"
              sx={{ margin: "10px 0 0" }}
              data-testid="referral-message"
            >
              {referralItem.referralMessage}
            </Typography>
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 2,
          width: "100%",
        }}
      >
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<CloseIcon />}
          sx={{ mr: 1 }}
          disabled={isSubmitting}
          onClick={e => {
            e.stopPropagation();
            handleModalOpen(referralItem);
          }}
          data-testid="referral-refuse-btn"
        >
          {tNs("referral.refuse")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<CheckIcon />}
          disabled={isSubmitting}
          onClick={e => {
            e.stopPropagation();
            onAcceptReferral(referralItem);
          }}
          data-testid="referral-accept-btn"
        >
          {tNs("referral.accept")}
        </Button>
      </Box>
      <ReferralRefusalModal
        modalOpen={modalOpen}
        handleModalClose={handleModalClose}
        modalData={modalData as IAlertTypeItemData}
        reasonOfRefusal={reasonOfRefusal}
        setReasonOfRefusal={setReasonOfRefusal}
        onRefuseReferral={onRefuseReferral}
        isSubmitting={isSubmitting}
      />
      <MessageTimeComponent variant="caption">
        {formatChatMessageTime(referralItem.updatedAt)}
      </MessageTimeComponent>
    </ListItem>
  );
};

export default ReferralAlertItem;
